/**
 * Kalender-Ansicht — offline-fähig, liest aus dem lokalen IndexedDB-Cache.
 *
 * Zeigt Schichten und Kalender-Einträge (Notiz/Frei/Urlaub/Krank/Wunschfrei).
 * Der Monat kommt aus dem geteilten Zeitraum-Store (store/period.ts).
 */

import { useMemo, useState } from 'react';
import { useShiftsForMonth } from '../data/shifts';
import { useCalendarEntriesForMonth } from '../data/calendarEntries';
import { useLocationsMap } from '../data/locations';
import type { LocalShift, LocalCalendarEntry } from '../db/schema';
import { MonthCalendar } from '../components/MonthCalendar';
import { DayDetailModal } from '../components/DayDetailModal';
import { ShiftFormModal } from '../components/ShiftFormModal';
import { EinspringenModal } from '../components/EinspringenModal';
import { EntryFormModal } from '../components/EntryFormModal';
import { ViewSwitcher } from '../components/ViewSwitcher';
import { formatMoney } from '../lib/format';
import { monthLabel } from '../lib/month';
import { usePeriod } from '../store/period';
import { summarizeSplit } from '../lib/payroll';
import { useAuth } from '../store/auth';

export default function CalendarPage() {
  const month = usePeriod((s) => s.anchorMonth);
  const prevMonth = usePeriod((s) => s.prev);
  const nextMonth = usePeriod((s) => s.next);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [creatingForDate, setCreatingForDate] = useState<string | null>(null);
  const [editing, setEditing] = useState<LocalShift | null>(null);
  const [creatingEntryForDate, setCreatingEntryForDate] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<LocalCalendarEntry | null>(null);
  // Phase F (Kapitel 11): "Einspringen" — unabhängig von einem bereits
  // geöffneten Tag erreichbar, da man den Tag ERST im Formular wählt.
  const [einspringen, setEinspringen] = useState(false);

  const shifts = useShiftsForMonth(month) ?? [];
  const entries = useCalendarEntriesForMonth(month) ?? [];
  const locationsById = useLocationsMap();
  const me = useAuth((s) => s.user);
  const breakDeduction = me?.breakDeductionEnabled ?? false;
  const split = useMemo(
    () => summarizeSplit(shifts, { breakDeduction }),
    [shifts, breakDeduction],
  );

  const dayShifts = openDay ? shifts.filter((s) => s.shiftDate === openDay) : [];
  const dayEntries = openDay ? entries.filter((e) => e.date === openDay) : [];

  return (
    <div className="max-w-3xl lg:max-w-6xl mx-auto space-y-3">
      <ViewSwitcher />

      <button
        onClick={() => setEinspringen(true)}
        className="w-full bg-amber-600/80 hover:bg-amber-600 transition rounded py-2 text-sm font-medium"
      >
        📞 Einspringen — Schicht spontan selbst eintragen
      </button>

      <div className="flex items-center justify-between card p-3">
        <button onClick={prevMonth} className="btn-ghost px-3 py-1" aria-label="Vorheriger Monat">
          ‹
        </button>
        <div className="text-center">
          <p className="font-semibold">{monthLabel(month)}</p>
          <p className="text-xs text-slate-400">
            {shifts.length} Schichten · {split.combined.totalHours.toFixed(2).replace('.', ',')} h ·{' '}
            <span className="text-emerald-400">{formatMoney(split.combined.totalWage)}</span>
          </p>
          {(split.done.totalHours > 0 || split.planned.totalHours > 0) && (
            <p className="text-[10px] text-slate-500">
              <span className="text-emerald-400">
                {split.done.totalHours.toFixed(2).replace('.', ',')} h gearb.
              </span>
              {split.planned.totalHours > 0 && (
                <>
                  {' + '}
                  <span className="text-blue-300">
                    {split.planned.totalHours.toFixed(2).replace('.', ',')} h gepl.
                  </span>
                </>
              )}
            </p>
          )}
        </div>
        <button onClick={nextMonth} className="btn-ghost px-3 py-1" aria-label="Nächster Monat">
          ›
        </button>
      </div>

      <MonthCalendar
        month={month}
        shifts={shifts}
        entries={entries}
        locationsById={locationsById}
        onDayClick={(iso) => setOpenDay(iso)}
        onShiftClick={(s) => setEditing(s)}
      />

      {openDay && (
        <DayDetailModal
          isoDate={openDay}
          shifts={dayShifts}
          entries={dayEntries}
          locationsById={locationsById}
          onClose={() => setOpenDay(null)}
          onCreate={() => {
            setCreatingForDate(openDay);
            setOpenDay(null);
          }}
          onEdit={(s) => {
            setEditing(s);
            setOpenDay(null);
          }}
          onCreateEntry={() => {
            setCreatingEntryForDate(openDay);
            setOpenDay(null);
          }}
          onEditEntry={(e) => {
            setEditingEntry(e);
            setOpenDay(null);
          }}
        />
      )}

      {creatingForDate && (
        <ShiftFormModal
          defaultDate={creatingForDate}
          onClose={() => setCreatingForDate(null)}
          onSaved={() => setCreatingForDate(null)}
        />
      )}

      {editing && (
        <ShiftFormModal
          shift={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}

      {creatingEntryForDate && (
        <EntryFormModal
          defaultDate={creatingEntryForDate}
          onClose={() => setCreatingEntryForDate(null)}
          onSaved={() => setCreatingEntryForDate(null)}
        />
      )}

      {editingEntry && (
        <EntryFormModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={() => setEditingEntry(null)}
        />
      )}

      {einspringen && (
        <EinspringenModal
          onClose={() => setEinspringen(false)}
          onSaved={() => setEinspringen(false)}
        />
      )}
    </div>
  );
}
